<template>
  <b-modal
    id="activity-history-modal"
    size="lg"
    hide-footer
    @show="fetchHistory"
  >
    <template v-slot:modal-header="{ close }">
      <b-container>
        <div class="modal-title">
          <h5 class="activity-history-title">{{ productName }}</h5>
          <div class="activity-history-subtitle">
            <span class="text-muted">Order Item ID</span>
            <span>{{ orderItemId }}</span>
          </div>
          <button type="button" aria-label="Close" class="close" @click="close">
            <i class="di-remove-10"></i>
          </button>
        </div>
      </b-container>
    </template>
    <div class="activity-history-body">
      <div v-if="loading" class="text-center py-4">
        <b-spinner small />
      </div>
      <div v-else-if="error" class="text-center py-4 text-muted">
        <p>Unable to load activity history</p>
        <b-button variant="outline-secondary" size="sm" @click="fetchHistory">
          Retry
        </b-button>
      </div>
      <div v-else-if="!history.length" class="text-center py-4 text-muted">
        No status history yet
      </div>
      <template v-else>
        <table class="table table-sm activity-history-table">
          <thead>
            <tr>
              <th style="width: 50%">Activity</th>
              <th style="width: 30%">Time</th>
              <th style="width: 20%">By</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in visibleHistory" :key="entry.id">
              <td>
                <div class="activity-name">
                  <span class="activity-bullet"></span>
                  {{ entry.status.display_name }}
                </div>
                <div v-if="entry.description" class="activity-description">
                  &#8627; {{ entry.description }}
                </div>
              </td>
              <td>{{ entry.date_time | moment('timezone', itemTimezone, 'D MMM YYYY, h:mm') }}</td>
              <td>{{ formatUser(entry.user) }}</td>
            </tr>
          </tbody>
        </table>
        <div v-if="history.length > visibleCount" class="text-center mt-3 mb-2">
          <b-button variant="outline-secondary" @click="showMore">
            Show more
          </b-button>
        </div>
      </template>
    </div>
  </b-modal>
</template>

<script>
import apiOrders from '@/api/orders';

const PAGE_SIZE = 10;

export default {
  name: 'ActivityHistoryModal',
  data() {
    return {
      loading: false,
      error: false,
      history: [],
      visibleCount: PAGE_SIZE,
      orderItemId: null,
      productName: '',
      itemTimezone: 'Australia/Sydney',
    };
  },
  computed: {
    visibleHistory() {
      return this.history.slice(0, this.visibleCount);
    },
  },
  mounted() {
    this.$root.$on('openActivityHistory', this.open);
  },
  beforeDestroy() {
    this.$root.$off('openActivityHistory', this.open);
  },
  methods: {
    open({ orderItemId, productName, timezone }) {
      this.orderItemId = orderItemId;
      this.productName = productName;
      this.itemTimezone = timezone;
      this.visibleCount = PAGE_SIZE;
      this.error = false;
      this.history = [];

      this.$bvModal.show('activity-history-modal');
    },
    fetchHistory() {
      this.loading = true;
      this.error = false;

      apiOrders
        .getOrderItemStatusHistory(this.orderItemId)
        .then(({ data }) => {
          this.history = data.data || [];
        })
        .catch(() => {
          this.error = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    showMore() {
      this.visibleCount += PAGE_SIZE;
    },
    formatUser(user) {
      if (!user) return 'System';
      if (user.first_name || user.last_name) {
        return [user.first_name, user.last_name].filter(Boolean).join(' ');
      }
      if (user.email) return user.email;
      return 'System';
    },
  },
};
</script>

<style scoped>
.activity-history-title {
  text-align: center;
  font-size: 22px;
  font-weight: 500;
  color: #2a2a2a;
  margin-bottom: 6px;
}

.activity-history-subtitle {
  text-align: center;
  font-size: 14px;
  gap: 6px;
  display: flex;
  justify-content: center;
}

.activity-history-body {
  padding: 0 20px 30px;
}

.activity-history-table {
  margin-bottom: 0;
}

.activity-history-table th {
  border-top: none;
  border-bottom: 1px solid #eaeaea;
  font-weight: 500;
  font-size: 14px;
  color: #2a2a2a;
  padding: 0 20px 10px;
}

.activity-history-table td {
  font-size: 14px;
  color: #2a2a2a;
  vertical-align: top;
  padding: 10px 20px;
  border-bottom: none;
}

.activity-history-table tbody tr {
  border-bottom: 1px solid #eaeaea;
}

.activity-history-table tbody tr:last-child {
  border-bottom: none;
}

.activity-bullet {
  display: inline-block;
  width: 6px;
  height: 6px;
  background-color: #6c757d;
  border-radius: 50%;
  margin-right: 8px;
  vertical-align: middle;
}

.activity-description {
  font-size: 12px;
  color: #2A68F5;
  padding-left: 14px;
  margin-top: 2px;
}
</style>
